import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Calendar, Clock, Share2, Linkedin, Twitter, Check, User, ArrowRight } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import DOMPurify from "dompurify";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { shareTemplates, getArticleBySlug, getAllArticles } from "../data/articles";

const ArticlePage = () => {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);
  const [copiedPlatform, setCopiedPlatform] = useState(null);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | FutureThought`;
    } else {
      document.title = "Article Not Found | FutureThought";
    }
    return () => { document.title = "FutureThought | B2B Technology Intelligence"; };
  }, [article]);

  const sanitizedContent = useMemo(
    () => article ? DOMPurify.sanitize(article.content, { ADD_ATTR: ['target', 'rel'] }) : "",
    [article?.content]
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    const all = getAllArticles().filter(a => a.slug !== article.slug);
    const sameCategory = all.filter(a => a.category === article.category);
    const others = all.filter(a => a.category !== article.category);
    return [...sameCategory, ...others].slice(0, 3);
  }, [article?.slug, article?.category]);

  if (!article) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-['Playfair_Display'] font-bold mb-4">Article Not Found</h1>
          <p className="text-[hsl(var(--muted-foreground))] mb-8">The article you're looking for doesn't exist.</p>
          <Link to="/" className="inline-flex items-center gap-2 text-[hsl(var(--primary))] hover:underline">
            <ArrowLeft size={18} /> Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const getShareText = (platform) => {
    const templates = shareTemplates[article.slug];
    if (!templates) return article.title;
    return templates[platform] || article.title;
  };

  const handleShare = (platform) => {
    const url = window.location.href;

    if (platform === 'linkedin') {
      const shareText = getShareText('linkedin');
      navigator.clipboard.writeText(`${shareText}\n\n${url}`);
      setCopiedPlatform('linkedin');
      toast.success("Share text copied — paste it in your LinkedIn post!");
      setTimeout(() => setCopiedPlatform(null), 2500);
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
    } else if (platform === 'twitter') {
      const shareText = getShareText('twitter');
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`, '_blank');
    } else if (platform === 'copy') {
      const shareText = getShareText('linkedin');
      navigator.clipboard.writeText(`${shareText}\n\n${url}`);
      setCopiedPlatform('copy');
      toast.success("Article link & share text copied to clipboard!");
      setTimeout(() => setCopiedPlatform(null), 2500);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Helmet>
        <title>{article.title} | FutureThought</title>
        <meta name="description" content={article.metaDescription || article.summary} />
        <meta name="author" content="Jit Singh" />
        <meta name="keywords" content={article.keywords || `${article.category}, technology, B2B intelligence, FutureThought, Jit Singh`} />
        <link rel="canonical" href={`https://futurethought.emergent.host/article/${article.slug}`} />
        <link rel="alternate" type="application/rss+xml" title="FutureThought RSS Feed" href="https://futurethought.emergent.host/api/rss" />

        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.metaDescription || article.summary} />
        <meta property="og:image" content={article.image} />
        <meta property="og:url" content={`https://futurethought.emergent.host/article/${article.slug}`} />
        <meta property="og:site_name" content="FutureThought" />
        <meta property="article:author" content="Jit Singh" />
        <meta property="article:section" content={article.category} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.metaDescription || article.summary} />
        <meta name="twitter:image" content={article.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.metaDescription || article.summary,
            "image": article.image,
            "datePublished": article.date,
            "author": {
              "@type": "Person",
              "name": "Jit Singh",
              "url": "https://futurethought.emergent.host"
            },
            "publisher": {
              "@type": "Organization",
              "name": "FutureThought",
              "url": "https://futurethought.emergent.host",
              "logo": {
                "@type": "ImageObject",
                "url": "https://futurethought.emergent.host/favicon.ico"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://futurethought.emergent.host/article/${article.slug}`
            },
            "articleSection": article.category,
            "wordCount": article.readTime?.replace(/\D/g, '') * 250 || 1000
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "FutureThought",
                "item": "https://futurethought.emergent.host"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": article.category,
                "item": `https://futurethought.emergent.host/#news`
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": article.title
              }
            ]
          })}
        </script>
      </Helmet>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="text-2xl font-bold font-['Playfair_Display']">
              Future<span className="text-[hsl(var(--primary))]">Thought</span>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
            >
              <ArrowLeft size={16} /> Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="pt-24 px-6 md:px-12 lg:px-24" data-testid="breadcrumb-nav">
        <nav aria-label="Breadcrumb" className="max-w-4xl mx-auto">
          <ol className="flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))]">
            <li>
              <Link to="/" className="hover:text-[hsl(var(--foreground))] transition-colors">FutureThought</Link>
            </li>
            <li className="select-none">/</li>
            <li>
              <Link to="/#news" className="hover:text-[hsl(var(--foreground))] transition-colors">{article.category}</Link>
            </li>
            <li className="select-none">/</li>
            <li className="text-[hsl(var(--foreground))] truncate max-w-[300px] md:max-w-none">{article.title}</li>
          </ol>
        </nav>
      </div>

      {/* Article Header */}
      <header className="pt-8 pb-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-sm font-mono tracking-wider uppercase text-[hsl(var(--primary))]">
              {article.category}
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-['Playfair_Display'] font-bold tracking-tight leading-tight mb-8">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-6 text-sm text-[hsl(var(--muted-foreground))]">
            <span className="flex items-center gap-2">
              <Calendar size={16} /> {article.date}
            </span>
            <span className="flex items-center gap-2">
              <Clock size={16} /> {article.readTime}
            </span>
            <span className="flex items-center gap-2">
              <User size={16} /> by Jit Singh
            </span>
          </div>
        </div>
      </header>

      {/* Featured Image */}
      <div className="px-6 md:px-12 lg:px-24 mb-12">
        <div className="max-w-5xl mx-auto">
          <div className="aspect-[21/9] overflow-hidden border border-[hsl(var(--border))]">
            <img
              src={article.image}
              alt={article.title}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="max-w-3xl mx-auto">
          <div 
            className="prose prose-lg prose-invert max-w-none
              [&>p]:text-lg [&>p]:leading-relaxed [&>p]:text-[hsl(var(--muted-foreground))] [&>p]:mb-6 [&>p]:text-justify
              [&>p.lead]:text-xl [&>p.lead]:text-[hsl(var(--foreground))] [&>p.lead]:font-medium [&>p.lead]:mb-8 [&>p.lead]:text-justify
              [&>p.coming-soon]:text-base [&>p.coming-soon]:text-[hsl(var(--primary))] [&>p.coming-soon]:mt-8 [&>p.coming-soon]:p-4 [&>p.coming-soon]:border [&>p.coming-soon]:border-[hsl(var(--primary)/0.3)] [&>p.coming-soon]:bg-[hsl(var(--primary)/0.05)] [&>p.coming-soon]:text-left
              [&>h2]:text-2xl [&>h2]:font-['Playfair_Display'] [&>h2]:font-semibold [&>h2]:mt-12 [&>h2]:mb-6 [&>h2]:text-[hsl(var(--foreground))]
              [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:mt-8 [&>h3]:mb-4 [&>h3]:text-[hsl(var(--foreground))]
              [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ul>li]:text-[hsl(var(--muted-foreground))] [&>ul>li]:mb-2
              [&>blockquote]:border-l-4 [&>blockquote]:border-[hsl(var(--primary))] [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:text-[hsl(var(--foreground))]
              [&_a]:text-[hsl(var(--primary))] [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:text-[hsl(var(--primary)/0.8)]"
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          />

          <Separator className="my-12 bg-[hsl(var(--border))]" />

          {/* Share Section */}
          <div className="flex flex-col gap-6">
            <div>
              <p className="text-sm font-mono tracking-wider uppercase text-[hsl(var(--muted-foreground))] mb-4">
                Share this article
              </p>
              
              {/* Share text preview */}
              {shareTemplates[article.slug] && (
                <div className="mb-5 p-4 bg-[hsl(var(--secondary)/0.3)] border border-[hsl(var(--border))] rounded-sm">
                  <p className="text-xs font-mono tracking-wider uppercase text-[hsl(var(--primary))] mb-2">
                    Suggested share text
                  </p>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed whitespace-pre-line line-clamp-4" data-testid="share-preview-text">
                    {shareTemplates[article.slug].linkedin}
                  </p>
                </div>
              )}

              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => handleShare('linkedin')}
                  className="border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] gap-2"
                  data-testid="share-linkedin-btn"
                >
                  {copiedPlatform === 'linkedin' ? <Check size={18} /> : <Linkedin size={18} />}
                  {copiedPlatform === 'linkedin' ? 'Copied & Opening' : 'Share on LinkedIn'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleShare('twitter')}
                  className="border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] gap-2"
                  data-testid="share-twitter-btn"
                >
                  <Twitter size={18} />
                  Share on X
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleShare('copy')}
                  className="border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]"
                  data-testid="share-copy-btn"
                >
                  {copiedPlatform === 'copy' ? <Check size={18} /> : <Share2 size={18} />}
                </Button>
              </div>
            </div>
            <div className="flex justify-end">
              <Link
                to="/#news"
                className="inline-flex items-center gap-2 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] px-6 py-3 font-medium hover:bg-[hsl(var(--primary)/0.9)] transition-all"
                data-testid="more-articles-btn"
              >
                More Articles
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 pb-16" data-testid="related-articles-section">
          <div className="max-w-7xl mx-auto">
            <Separator className="mb-16 bg-[hsl(var(--border))]" />
            <div className="mb-10">
              <p className="text-sm font-mono tracking-wider uppercase text-[hsl(var(--primary))] mb-3">
                Keep Reading
              </p>
              <h2 className="text-3xl md:text-4xl font-['Playfair_Display'] font-semibold tracking-tight">
                Related Articles
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedArticles.map((related) => (
                <Link
                  key={related.slug}
                  to={`/article/${related.slug}`}
                  className="group block"
                  data-testid={`related-article-${related.slug}`}
                  onClick={() => window.scrollTo(0, 0)}
                >
                  <article className="h-full border border-[hsl(var(--border))] hover:border-[hsl(var(--primary)/0.5)] transition-all duration-300 overflow-hidden bg-[hsl(var(--card))]">
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <span className="text-xs font-mono tracking-wider uppercase text-[hsl(var(--primary))]">
                        {related.category}
                      </span>
                      <h3 className="text-lg font-['Playfair_Display'] font-medium mt-2 mb-3 leading-snug group-hover:text-[hsl(var(--primary))] transition-colors line-clamp-2">
                        {related.title}
                      </h3>
                      <div className="flex items-center justify-between text-xs text-[hsl(var(--muted-foreground))]">
                        <span className="flex items-center gap-1">
                          <Calendar size={12} /> {related.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={12} /> {related.readTime}
                        </span>
                      </div>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--primary))] group-hover:gap-2.5 transition-all">
                        Read Article <ArrowRight size={14} />
                      </span>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 lg:px-24 border-t border-[hsl(var(--border))]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/" className="text-xl font-bold font-['Playfair_Display']">
            Future<span className="text-[hsl(var(--primary))]">Thought</span>
          </Link>
          <div className="flex gap-6 text-sm text-[hsl(var(--muted-foreground))]">
            <Link to="/privacy-policy" className="hover:text-[hsl(var(--foreground))] transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[hsl(var(--foreground))] transition-colors">Terms of Service</Link>
            <Link to="/contact" className="hover:text-[hsl(var(--foreground))] transition-colors">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ArticlePage;
